import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import "./styles.css";

export interface MultiSelectOption {
  value: string | number;
  text: string;
  html?: string;
  selected?: boolean;
  disabled?: boolean;
  group?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  placeholder?: string;
  search?: boolean;
  selectAll?: boolean;
  maxSelections?: number;
  minSelections?: number;
  maxDisplayItems?: number;
  width?: string;
  height?: string;
  className?: string;
  disabled?: boolean;
  value?: (string | number)[];
  onChange?: (
    selectedValues: (string | number)[],
    selectedItems: MultiSelectOption[]
  ) => void;
  onSelect?: (
    value: string | number,
    text: string,
    option: MultiSelectOption
  ) => void;
  onUnselect?: (
    value: string | number,
    text: string,
    option: MultiSelectOption
  ) => void;
  onSearch?: (searchTerm: string) => void;
  showBadges?: boolean;
  clearable?: boolean;
}

export default function MultiSelect({
  options = [],
  placeholder = "Select options",
  search = true,
  selectAll = true,
  maxSelections,
  minSelections,
  maxDisplayItems,
  width,
  height,
  className = "",
  disabled = false,
  value = [],
  onChange,
  onSelect,
  onUnselect,
  onSearch,
  showBadges = false,
  clearable = true,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] =
    useState<(string | number)[]>(value);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [limitMessage, setLimitMessage] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const dropdownId = useRef(
    `multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!search || !searchTerm.trim()) return options;

    const searchTerms = searchTerm
      .toLowerCase()
      .split(",")
      .map((term) => term.trim())
      .filter(Boolean);

    return options.filter((option) => {
      const optionText = option.text.toLowerCase();
      return searchTerms.some((term) => optionText.includes(term));
    });
  }, [searchTerm, options, search]);

  const selectedItems = useMemo(
    () => options.filter((option) => selectedValues.includes(option.value)),
    [options, selectedValues]
  );

  useEffect(() => {
    if (onSearch && searchTerm) {
      onSearch(searchTerm);
    }
  }, [searchTerm, onSearch]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const availableOptions = filteredOptions.filter((opt) => !opt.disabled);

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;

        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev < availableOptions.length - 1 ? prev + 1 : 0;
            scrollToOption(nextIndex);
            return nextIndex;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : availableOptions.length - 1;
            scrollToOption(nextIndex);
            return nextIndex;
          });
          break;

        case "Enter":
        case " ":
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < availableOptions.length) {
            handleOptionToggle(availableOptions[focusedIndex]);
          }
          break;

        case "a":
          if ((e.ctrlKey || e.metaKey) && selectAll && maxSelections !== 1) {
            e.preventDefault();
            handleSelectAll();
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, focusedIndex, filteredOptions, maxSelections]);

  const scrollToOption = (index: number) => {
    if (!optionsRef.current) return;

    const optionElements = optionsRef.current.querySelectorAll(
      ".multi-select-option"
    );
    const targetOption = optionElements[index] as HTMLElement;

    if (targetOption) {
      targetOption.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  };

  const showLimitMessage = useCallback((message: string) => {
    setLimitMessage(message);
    setTimeout(() => setLimitMessage(""), 3000);
  }, []);

  const handleToggleDropdown = () => {
    if (!disabled) {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (newIsOpen && search) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }

      if (!newIsOpen) {
        setFocusedIndex(-1);
      }
    }
  };

  const handleOptionToggle = (option: MultiSelectOption) => {
    if (option.disabled) return;

    const isCurrentlySelected = selectedValues.includes(option.value);
    let newSelectedValues: (string | number)[];

    if (isCurrentlySelected) {

      if (minSelections && selectedValues.length <= minSelections) {
        showLimitMessage(
          `Minimum ${minSelections} selection${
            minSelections > 1 ? "s" : ""
          } required`
        );
        return;
      }
      newSelectedValues = selectedValues.filter((val) => val !== option.value);
      onUnselect?.(option.value, option.text, option);
    } else {

      if (maxSelections && selectedValues.length >= maxSelections) {
        if (maxSelections === 1) {

          newSelectedValues = [option.value];
          if (selectedValues.length > 0) {
            const previousOption = options.find((opt) =>
              selectedValues.includes(opt.value)
            );
            if (previousOption) {
              onUnselect?.(
                previousOption.value,
                previousOption.text,
                previousOption
              );
            }
          }
        } else {
          showLimitMessage(`Maximum ${maxSelections} selections allowed`);
          return;
        }
      } else {
        newSelectedValues = [...selectedValues, option.value];
      }
      onSelect?.(option.value, option.text, option);
    }

    setSelectedValues(newSelectedValues);
    const newSelectedItems = options.filter((opt) =>
      newSelectedValues.includes(opt.value)
    );
    onChange?.(newSelectedValues, newSelectedItems);


    if (maxSelections === 1 && !isCurrentlySelected) {
      setTimeout(() => {
        setIsOpen(false);
        setFocusedIndex(-1);
      }, 150);
    }
  };

  const handleSelectAll = () => {
    const availableOptions = filteredOptions.filter(
      (option) => !option.disabled
    );
    let allValues = availableOptions.map((option) => option.value);

    if (maxSelections && allValues.length > maxSelections) {
      allValues = allValues.slice(0, maxSelections);
      showLimitMessage(`Selected first ${maxSelections} items (limit reached)`);
    } else if (maxDisplayItems && allValues.length > maxDisplayItems) {
      allValues = allValues.slice(0, maxDisplayItems);
    }

    setSelectedValues(allValues);
    const limitedItems = options.filter((opt) => allValues.includes(opt.value));
    onChange?.(allValues, limitedItems);
  };

  const handleDeselectAll = () => {
    if (minSelections && minSelections > 0) {
      showLimitMessage(
        `Minimum ${minSelections} selection${
          minSelections > 1 ? "s" : ""
        } required`
      );
      return;
    }
    setSelectedValues([]);
    onChange?.([], []);
  };

  const handleRemoveBadge = (
    optionValue: string | number,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    const option = options.find((opt) => opt.value === optionValue);
    if (option) {
      handleOptionToggle(option);
    }
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeselectAll();
  };

  const getDisplayText = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    if (maxSelections === 1 || selectedItems.length === 1) {
      return selectedItems[0].text;
    }
    return `${selectedItems.length} item${
      selectedItems.length > 1 ? "s" : ""
    } selected`;
  };

  const canSelectAll = filteredOptions.some(
    (option) => !selectedValues.includes(option.value) && !option.disabled
  );

  const canDeselectAll =
    selectedValues.length > 0 &&
    (!minSelections || selectedValues.length > minSelections);

  const showSelectAllButton =
    selectAll && filteredOptions.length > 1 && maxSelections !== 1;
  const isSingleSelect = maxSelections === 1;

  return (
    <div
      ref={containerRef}
      className={`multi-select ${className} ${disabled ? "disabled" : ""} ${
        isSingleSelect ? "single-select" : ""
      }`}
      style={{ width, height }}
    >
      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={dropdownId.current}
        aria-disabled={disabled}
        aria-label={placeholder}
        className={`multi-select-header ${isOpen ? "open" : ""}`}
        onClick={handleToggleDropdown}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleDropdown();
          }
        }}
      >
        {showBadges && selectedItems.length > 0 && !isSingleSelect ? (
          <div className="multi-select-badges">
            {selectedItems.map((item) => (
              <span key={item.value} className="multi-select-badge">
                {item.text}
                <button
                  type="button"
                  className="multi-select-badge-remove"
                  onClick={(e) => handleRemoveBadge(item.value, e)}
                  aria-label={`Remove ${item.text}`}
                  tabIndex={-1}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="multi-select-text">{getDisplayText()}</span>
        )}

        <div className="multi-select-actions">
          {clearable && selectedValues.length > 0 && !minSelections && (
            <button
              type="button"
              className="multi-select-clear"
              onClick={handleClearAll}
              aria-label="Clear all selections"
              tabIndex={-1}
            >
              ×
            </button>
          )}
          <span className={`multi-select-arrow ${isOpen ? "up" : "down"}`}>
            ▼
          </span>
        </div>
      </div>

      {limitMessage && (
        <div className="multi-select-limit-message" role="alert">
          {limitMessage}
        </div>
      )}

      {isOpen && (
        <div
          id={dropdownId.current}
          role="listbox"
          aria-multiselectable={!isSingleSelect}
          className="multi-select-dropdown"
        >
          {search && (
            <div className="multi-select-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search... (use commas for multiple terms)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="multi-select-search-input"
                onClick={(e) => e.stopPropagation()}
                aria-label="Search options"
              />
            </div>
          )}

          {showSelectAllButton && (
            <div className="multi-select-controls">
              {canSelectAll && (
                <button
                  type="button"
                  className="multi-select-action-btn"
                  onClick={handleSelectAll}
                  disabled={
                    maxSelections
                      ? selectedValues.length >= maxSelections
                      : false
                  }
                >
                  Select All
                </button>
              )}
              {canDeselectAll && (
                <button
                  type="button"
                  className="multi-select-action-btn"
                  onClick={handleDeselectAll}
                >
                  Deselect All
                </button>
              )}
            </div>
          )}

          <div ref={optionsRef} className="multi-select-options">
            {filteredOptions.length === 0 ? (
              <div className="multi-select-no-options">No options found</div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = selectedValues.includes(option.value);
                const isFocused = focusedIndex === index;

                return (
                  <label
                    key={`${option.value}-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled}
                    className={`multi-select-option ${
                      isSelected ? "selected" : ""
                    } ${option.disabled ? "disabled" : ""} ${
                      isFocused ? "focused" : ""
                    }`}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <input
                      type={isSingleSelect ? "radio" : "checkbox"}
                      className="multi-select-input"
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option)}
                      disabled={option.disabled}
                      tabIndex={-1}
                      aria-label={option.text}
                    />
                    <span className="multi-select-option-text">
                      {option.html ? (
                        <span
                          dangerouslySetInnerHTML={{ __html: option.html }}
                        />
                      ) : (
                        option.text
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>

          {(minSelections || maxSelections || maxDisplayItems) && (
            <div className="multi-select-info">
              {minSelections && <span>Min: {minSelections}</span>}
              {maxSelections && <span>Max: {maxSelections}</span>}
              {maxDisplayItems && <span>Display: {maxDisplayItems}</span>}
              <span className="multi-select-info-count">
                Selected: {selectedValues.length}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
